// Kullanıcı ekleme/düzenleme modal'ı
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { FiX } from 'react-icons/fi';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const UserModal = ({ user, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const { user: currentUser, logout, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [managers, setManagers] = useState([]);
  const [showLogoutAfterEdit, setShowLogoutAfterEdit] = useState(false);
  const [logoutCountdown, setLogoutCountdown] = useState(0);
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm();

  // Yönetici listesini al
  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const response = await userService.getManagers();
        setManagers(response.managers || []);
      } catch (error) {
        console.error('Yönetici listesi alınamadı:', error);
      }
    };

    fetchManagers();
  }, []);

  // Kullanıcı oluşturma/güncelleme mutation'ı
  const saveUserMutation = useMutation(
    (data) => {
      if (user) {
        return userService.updateUser(user.user_uuid, data);
      } else {
        return userService.createUser(data);
      }
    },
    {
      onSuccess: (data) => {
        // Toast mesajını sadece bir kere göster
        toast.success(user ? 'Kullanıcı güncellendi' : 'Kullanıcı oluşturuldu');
        queryClient.invalidateQueries('users');
        
        // Debug: Kullanıcı ID'lerini kontrol et
        
        // Kendi kullanıcı düzenleniyorsa logout uyarısı göster
        if (user && currentUser && user.id === currentUser.id) {
          
          // Kullanıcı bilgilerini güncelle (rol değişikliği için)
          const updatedUserData = {
            ...currentUser,
            user_type: data.user_type,
            user_department: data.user_department,
            user_manager: data.user_manager
          };
          updateUser(updatedUserData);
          
          // State'leri güncelle
          setShowLogoutAfterEdit(true);
          setLogoutCountdown(5);
        } else {
          onSuccess();
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'İşlem başarısız');
      }
    }
  );

  // Şifre değişiklik fonksiyonu
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Şifre doğrulama
  const validatePassword = () => {
    if (!user && (!passwordData.password || !passwordData.confirmPassword)) {
      return 'Şifre alanları gerekli';
    }
    if (passwordData.password && passwordData.password.length < 6) {
      return 'Şifre en az 6 karakter olmalı';
    }
    if (passwordData.password !== passwordData.confirmPassword) {
      return 'Şifreler eşleşmiyor';
    }
    return null;
  };

  // Form'u kullanıcı verileriyle doldur
  useEffect(() => {
    if (user) {
      setValue('user_fullname', user.user_fullname);
      setValue('user_username', user.user_username);
      setValue('user_email', user.user_email);
      setValue('user_department', user.user_department);
      setValue('user_type', user.user_type);
      setValue('user_gsm', user.user_gsm);
      setValue('user_manager', user.user_manager);
      setValue('user_status', user.user_status);
    }
  }, [user, setValue]);

  // Managers yüklendikten sonra user_manager değerini tekrar set et
  useEffect(() => {
    if (user && managers.length > 0 && user.user_manager) {
      setValue('user_manager', user.user_manager);
    }
  }, [managers, user, setValue]);

  // Geri sayım useEffect'i
  useEffect(() => {
    let interval;
    if (showLogoutAfterEdit && logoutCountdown > 0) {
      interval = setInterval(() => {
        setLogoutCountdown(prev => {
          if (prev <= 1) {
            // Geri sayım bitti, logout yap
            setShowLogoutAfterEdit(false);
            logout(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showLogoutAfterEdit, logoutCountdown, logout]);

  // Form gönderimi
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      
      // Şifre doğrulama
      const passwordError = validatePassword();
      if (passwordError) {
        toast.error(passwordError);
        return;
      }

      // Yeni kullanıcı için şifre ekle (düzenleme sırasında şifre gönderme)
      if (!user && passwordData.password) {
        data.password = passwordData.password;
      }
      
      // Düzenleme sırasında şifre alanlarını kaldır
      if (user) {
        delete data.password;
        delete data.confirmPassword;
      }

      await saveUserMutation.mutateAsync(data);
      // Toast mesajı mutation'da gösteriliyor, burada gösterme
      // onSuccess() çağrısı da mutation'da yapılıyor
    } catch (error) {
      console.error('Form gönderim hatası:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className={`modal-content max-w-4xl ${showLogoutAfterEdit ? 'hidden' : ''}`}>
        {/* Modal header */}
        <div className="modal-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">
              {user ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-300"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* Modal body */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ad Soyad */}
            <div>
              <label htmlFor="user_fullname" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ad Soyad *
              </label>
              <input
                id="user_fullname"
                type="text"
                {...register('user_fullname', { required: 'Ad soyad gerekli' })}
                className={`input-field ${errors.user_fullname ? 'input-error' : ''}`}
                placeholder="Ad soyad girin"
                autoComplete="off"
                data-form-type="other"
              />
              {errors.user_fullname && (
                <p className="mt-1 text-sm text-red-600">{errors.user_fullname.message}</p>
              )}
            </div>

            {/* Kullanıcı adı */}
            <div>
              <label htmlFor="user_username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Kullanıcı Adı *
              </label>
              <input
                id="user_username"
                type="text"
                {...register('user_username', { 
                  required: 'Kullanıcı adı gerekli',
                  minLength: { value: 3, message: 'En az 3 karakter olmalı' }
                })}
                className={`input-field ${errors.user_username ? 'input-error' : ''}`}
                placeholder="Kullanıcı adını girin"
                disabled={!!user} // Mevcut kullanıcıda değiştirilemez
                autoComplete="off"
                data-form-type="other"
              />
              {errors.user_username && (
                <p className="mt-1 text-sm text-red-600">{errors.user_username.message}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="user_email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email *
              </label>
              <input
                id="user_email"
                type="email"
                {...register('user_email', { 
                  required: 'Email gerekli',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Geçerli email adresi giriniz'
                  }
                })}
                className={`input-field ${errors.user_email ? 'input-error' : ''}`}
                placeholder="Email adresini girin"
                autoComplete="off"
                data-form-type="other"
              />
              {errors.user_email && (
                <p className="mt-1 text-sm text-red-600">{errors.user_email.message}</p>
              )}
            </div>

            {/* Telefon */}
            <div>
              <label htmlFor="user_gsm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Telefon *
              </label>
              <input
                id="user_gsm"
                type="tel"
                {...register('user_gsm', { required: 'Telefon numarası gerekli' })}
                className={`input-field ${errors.user_gsm ? 'input-error' : ''}`}
                placeholder="Telefon numarasını girin"
                autoComplete="off"
                data-form-type="other"
              />
              {errors.user_gsm && (
                <p className="mt-1 text-sm text-red-600">{errors.user_gsm.message}</p>
              )}
            </div>

            {/* Şifre - Sadece yeni kullanıcı için */}
            {!user && (
              <>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Şifre *
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={passwordData.password}
                    onChange={(e) => handlePasswordChange('password', e.target.value)}
                    className="input-field"
                    placeholder="Şifre girin"
                    required
                    autoComplete="new-password"
                    data-form-type="other"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Şifre Tekrar *
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    className="input-field"
                    placeholder="Şifreyi tekrar girin"
                    required
                    autoComplete="new-password"
                    data-form-type="other"
                  />
                </div>
              </>
            )}

            {/* Departman */}
            <div>
              <label htmlFor="user_department" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Departman *
              </label>
              <select
                id="user_department"
                {...register('user_department', { required: 'Departman seçimi gerekli' })}
                className={`input-field ${errors.user_department ? 'input-error' : ''}`}
                autoComplete="off"
                data-form-type="other"
              >
                <option value="">Departman seçin</option>
                <option value="Satış">Satış</option>
                <option value="Pazarlama">Pazarlama</option>
                <option value="İnsan Kaynakları">İnsan Kaynakları</option>
                <option value="Muhasebe">Muhasebe</option>
                <option value="IT">IT</option>
                <option value="Yönetim">Yönetim</option>
              </select>
              {errors.user_department && (
                <p className="mt-1 text-sm text-red-600">{errors.user_department.message}</p>
              )}
            </div>

            {/* Kullanıcı tipi */}
            <div>
              <label htmlFor="user_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Kullanıcı Tipi *
              </label>
              <select
                id="user_type"
                {...register('user_type', { required: 'Kullanıcı tipi seçimi gerekli' })}
                className={`input-field relative z-50 ${errors.user_type ? 'input-error' : ''}`}
                disabled={user && currentUser && user.id === currentUser.id}
                autoComplete="off"
                data-form-type="other"
              >
                <option value="">Tip seçin</option>
                {/* Admin kullanıcı kendini düzenlerken sadece admin seçeneği göster */}
                {user && currentUser && user.id === currentUser.id ? (
                  <option value="admin">Admin</option>
                ) : (
                  <>
                    {/* Admin kullanıcı başkalarını manager yapamaz */}
                    {currentUser?.user_type !== 'admin' && <option value="manager">Manager</option>}
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                    <option value="guest">Guest</option>
                  </>
                )}
              </select>
              {errors.user_type && (
                <p className="mt-1 text-sm text-red-600">{errors.user_type.message}</p>
              )}
            </div>

            {/* Yönetici */}
            <div>
              <label htmlFor="user_manager" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Yönetici
              </label>
              <select
                id="user_manager"
                {...register('user_manager')}
                className="input-field relative z-50"
                autoComplete="off"
                data-form-type="other"
              >
                <option value="">Yönetici seçin</option>
                {managers.map((manager) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.user_fullname} - {manager.user_department}
                  </option>
                ))}
              </select>
            </div>

            {/* Durum */}
            <div>
              <label htmlFor="user_status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Durum
              </label>
              <select
                id="user_status"
                {...register('user_status')}
                className="input-field"
              >
                <option value={1}>Aktif</option>
                <option value={0}>Pasif</option>
              </select>
            </div>
          </div>

          {/* Modal footer */}
          <div className="flex items-center justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              İptal
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Kaydediliyor...' : (user ? 'Güncelle' : 'Oluştur')}
            </button>
          </div>
        </form>
      </div>

      {/* Logout After Edit Modal */}
      {showLogoutAfterEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="modal-content max-w-md">
            {/* Modal header */}
            <div className="modal-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  Profil Güncellendi
                </h3>
              </div>
            </div>
            
            {/* Modal body */}
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Profiliniz başarıyla güncellendi. Değişikliklerin etkili olması için çıkış yapıp tekrar giriş yapmanız gerekiyor.
              </p>
              
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    setShowLogoutAfterEdit(false);
                    setLogoutCountdown(0);
                    logout();
                  }}
                  className="btn-primary"
                >
                  {logoutCountdown > 0 ? `Tamam (${logoutCountdown})` : "Tamam"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserModal;
